import { forwardRef, useId, useImperativeHandle } from 'react';
import { useComputedColorScheme, useMantineColorScheme } from '@mantine/core';
import { useThemeReveal } from '../../hooks/useThemeReveal';
import './ThemeSwitch.scss';

// SVG-маска и хореография перехода адаптированы из GUI Challenge Адама Аргайла:
// https://web.dev/articles/building/a-theme-switch-component
// Пример кода опубликован под Apache License 2.0.
export interface ThemeSwitchHandle {
    followSystemTheme: () => Promise<void>;
}

export interface IThemeSwitchProps {
    onToggle?: () => void;
}

const ThemeSwitch = forwardRef<ThemeSwitchHandle, IThemeSwitchProps>(function ThemeSwitch({ onToggle }, controlRef) {
    const maskId = `rf-theme-switch-mask-${useId().replaceAll(':', '')}`;
    const computedColorScheme = useComputedColorScheme('light', { getInitialValueInEffect: false });
    // Mantine по умолчанию на несколько миллисекунд отключает все transition при смене темы.
    // Здесь переход — часть смысла контрола, поэтому сохраняем его.
    const { setColorScheme } = useMantineColorScheme({ keepTransitions: true });
    const isDark = computedColorScheme === 'dark';
    const nextSchemeLabel = isDark ? 'Включить светлую тему' : 'Включить тёмную тему';
    const { ref, reveal } = useThemeReveal<HTMLButtonElement>();

    useImperativeHandle(
        controlRef,
        () => ({
            followSystemTheme: async () => {
                const systemIsDark =
                    typeof window.matchMedia === 'function' &&
                    window.matchMedia('(prefers-color-scheme: dark)').matches;

                if (systemIsDark === isDark) {
                    setColorScheme('auto');
                    return;
                }

                await reveal(() => setColorScheme('auto'));
            },
        }),
        [isDark, setColorScheme, reveal],
    );

    return (
        <button
            ref={ref}
            type="button"
            className="rf-theme-switch"
            data-color-scheme={computedColorScheme}
            data-testid="user-settings-theme-switch"
            aria-label={nextSchemeLabel}
            aria-live="polite"
            title={nextSchemeLabel}
            onClick={() => {
                onToggle?.();
                void reveal(() => setColorScheme(isDark ? 'light' : 'dark'));
            }}
        >
            <svg
                className="rf-theme-switch__sun-and-moon"
                aria-hidden="true"
                width="28"
                height="28"
                viewBox="0 0 24 24"
            >
                <circle className="rf-theme-switch__sun" cx="12" cy="12" r="6" mask={`url(#${maskId})`} />
                <g className="rf-theme-switch__sun-beams">
                    <line x1="12" y1="1" x2="12" y2="3" />
                    <line x1="12" y1="21" x2="12" y2="23" />
                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                    <line x1="1" y1="12" x2="3" y2="12" />
                    <line x1="21" y1="12" x2="23" y2="12" />
                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                </g>
                <mask className="rf-theme-switch__moon" id={maskId}>
                    <rect x="0" y="0" width="100%" height="100%" fill="white" />
                    <circle cx="24" cy="10" r="6" fill="black" />
                </mask>
            </svg>
        </button>
    );
});

export default ThemeSwitch;
